import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronDown, Search } from "lucide-react";
import { PAYS, chercherPays } from "@/lib/pays";

// Facture internationale : dates au format américain MM/DD/YYYY.
const iso2us = (iso) => {
  const m = (iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
};
function us2iso(t) {
  const m = (t || "").trim().match(/^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{2}|\d{4})$/);
  if (!m) return null;
  const a = m[3].length === 2 ? `20${m[3]}` : m[3];
  const d = new Date(Date.UTC(+a, +m[1] - 1, +m[2]));
  if (d.getUTCMonth() !== +m[1] - 1 || d.getUTCDate() !== +m[2]) return null;
  return `${a}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
}

/** Date au format MM/DD/YYYY (quelle que soit la langue du navigateur), avec calendrier. */
export function ChampDate({ value, onChange, className = "", testId }) {
  const [texte, setTexte] = useState(iso2us(value));
  const cal = useRef(null);
  useEffect(() => { setTexte(iso2us(value)); }, [value]);
  const valide = !texte || us2iso(texte);
  return (
    <div className="relative">
      <input value={texte} placeholder="MM/DD/YYYY" inputMode="numeric" data-testid={testId}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d/]/g, "").slice(0, 10);
          setTexte(v);
          const iso = us2iso(v);
          if (iso) onChange(iso); else if (!v) onChange("");
        }}
        onBlur={() => { if (!us2iso(texte)) setTexte(iso2us(value)); }}
        className={`${className} pr-8 ${valide ? "" : "!border-red-500"}`} />
      <button type="button" className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-neutral-900 print:hidden" title="Calendrier"
        onClick={() => { try { cal.current?.showPicker(); } catch { cal.current?.click(); } }}>
        <CalendarDays size={15} />
      </button>
      <input ref={cal} type="date" tabIndex={-1} aria-hidden="true" value={value || ""} onChange={(e) => e.target.value && onChange(e.target.value)}
        className="absolute right-0 bottom-0 w-0 h-0 opacity-0 pointer-events-none" />
    </div>
  );
}

/** Pays : liste déroulante avec recherche (français ou anglais) ; le nom inscrit est en anglais. */
export function SelectPays({ value, onChange, className = "", testId }) {
  const [ouvert, setOuvert] = useState(false);
  const [q, setQ] = useState("");
  const [actif, setActif] = useState(0);
  const boite = useRef(null);
  const liste = useMemo(() => chercherPays(q), [q]);
  const fr = PAYS.find((p) => p[1] === value)?.[2];
  useEffect(() => {
    if (!ouvert) return undefined;
    const fermer = (e) => { if (!boite.current?.contains(e.target)) setOuvert(false); };
    document.addEventListener("mousedown", fermer);
    return () => document.removeEventListener("mousedown", fermer);
  }, [ouvert]);
  const choisir = (p) => { onChange(p[1]); setOuvert(false); setQ(""); };
  return (
    <div className="relative" ref={boite}>
      <button type="button" onClick={() => { setOuvert((o) => !o); setActif(0); }} data-testid={testId}
        className={`${className} flex items-center justify-between text-left`}>
        <span className={value ? "" : "text-neutral-400"}>{value || "Choisir un pays…"}{fr && fr !== value && <span className="text-neutral-400 text-xs"> ({fr})</span>}</span>
        <ChevronDown size={14} className="text-neutral-500 shrink-0 print:hidden" />
      </button>
      {ouvert && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-md border border-neutral-300 bg-white shadow-lg print:hidden">
          <div className="relative border-b border-neutral-200">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setActif(0); }} placeholder="Rechercher un pays (français ou anglais)…"
              className="w-full pl-7 pr-2 py-2 text-sm outline-none bg-transparent" data-testid={`${testId}-recherche`}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setActif((a) => Math.min(a + 1, liste.length - 1)); }
                if (e.key === "ArrowUp") { e.preventDefault(); setActif((a) => Math.max(a - 1, 0)); }
                if (e.key === "Enter") { e.preventDefault(); if (liste[actif]) choisir(liste[actif]); }
                if (e.key === "Escape") setOuvert(false);
              }} />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1 text-sm">
            {liste.map((p, i) => (
              <li key={p[0]}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choisir(p)} data-testid={`pays-${p[0]}`}
                  className={`w-full text-left px-3 py-1.5 ${i === actif ? "bg-sky-100" : "hover:bg-neutral-100"} ${p[1] === value ? "font-semibold" : ""}`}>
                  {p[1]}{p[2] !== p[1] && <span className="text-neutral-400 text-xs"> — {p[2]}</span>}
                </button>
              </li>
            ))}
            {!liste.length && <li className="px-3 py-2 text-neutral-500">Aucun pays.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
